"use client";

import Link from "next/link";
import { useParams } from "next/navigation";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  ApiError,
  api,
  isAccessGatedProfile,
  loadTokens,
  type AccessGatedProfileResponse,
  type CopySubscription,
  type PostRecord,
  type ProfileVisibility,
  type PublicProfileResponse,
} from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteSidebar } from "@/components/SiteSidebar";
import { ComposerButton } from "@/components/ComposerButton";
import { ActivityTimeline } from "@/components/social/ActivityTimeline";
import { FollowButton } from "@/components/social/FollowButton";
import { SubscribeButton } from "@/components/posts/SubscribeButton";
import { VerifiedExpertBadge } from "@/components/users/VerifiedExpertBadge";
import { SubscribeToPiModal } from "@/components/SubscribeToPiModal";
import { useDisplayCurrency } from "@/lib/useDisplayCurrency";
import { useFxRate } from "@/lib/useFxRate";
import { formatUsdcCentsForDisplay } from "@/lib/format";
import { piTierMeta } from "@/lib/piTier";

/**
 * EPIC-021 STORY-381 + STORY-382 — general-user public profile.
 *
 * Lives at /u/[username]. Companion to /pi/[username] (PI-only). Shows
 * display name + avatar + bio + follower-counts + (privacy-gated)
 * lifetime stats + recent trades. Default backend visibility is
 * `private` so closed-beta users opt in rather than out.
 *
 * When the viewer is privacy-gated out, backend returns the minimal
 * AccessGatedProfileResponse — we render a "this profile is private"
 * placeholder rather than 404 (404 would leak existence by URL).
 */
export default function PublicUserProfilePage() {
  const { t } = useTranslation();
  const params = useParams<{ username: string }>();
  const username = params?.username;
  const [profile, setProfile] = useState<
    PublicProfileResponse | AccessGatedProfileResponse | null
  >(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!username) return;
    let cancelled = false;
    void api
      .getPublicProfile(username)
      .then((p) => {
        if (!cancelled) setProfile(p);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setError(t("publicProfile.errNotFound"));
        } else {
          setError(
            err instanceof Error ? err.message : t("publicProfile.errLoad"),
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [username, t]);

  return (
    <>
      <SiteHeader />
      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-5 px-5 py-4 md:grid-cols-[200px_1fr]">
        <SiteSidebar />
        <main className="min-w-0 max-w-3xl">
          <div className="mb-4 flex items-center gap-3">
            <Link
              href="/markets"
              className="text-[12px] text-[var(--color-text3)] hover:text-[var(--color-text)]"
            >
              {t("publicProfile.backToMarkets")}
            </Link>
          </div>

          {error && (
            <p
              role="alert"
              className="mb-4 text-[12px]"
              style={{ color: "var(--color-no)" }}
            >
              {error}
            </p>
          )}

          {!profile && !error && (
            <p className="text-[12px] text-[var(--color-text3)]">
              {t("publicProfile.loading")}
            </p>
          )}

          {profile && isAccessGatedProfile(profile) && (
            <PrivateProfilePlaceholder
              username={profile.user.username}
              visibility={profile.visibility}
              t={t}
            />
          )}

          {profile && !isAccessGatedProfile(profile) && (
            <FullProfile profile={profile} t={t} />
          )}
        </main>
      </div>
    </>
  );
}

function PrivateProfilePlaceholder({
  username,
  visibility,
  t,
}: {
  username: string;
  visibility: ProfileVisibility;
  t: TFunction;
}) {
  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
      <div className="flex items-center gap-3">
        <div
          className="flex h-[56px] w-[56px] flex-shrink-0 items-center justify-center rounded-full text-[24px] font-bold"
          style={{
            background:
              "color-mix(in srgb, var(--color-text3) 14%, transparent)",
            color: "var(--color-text3)",
          }}
          aria-hidden="true"
        >
          {username.charAt(0).toUpperCase()}
        </div>
        <div>
          <h1 className="text-[22px] font-bold tracking-tight">{username}</h1>
          <p className="mt-1 text-[12px] text-[var(--color-text3)]">
            {visibility === "followers"
              ? t("publicProfile.followersOnly")
              : t("publicProfile.private")}
          </p>
        </div>
      </div>
      {visibility === "followers" && (
        <div className="mt-4">
          <FollowButton username={username} />
          <p className="mt-2 text-[11px] text-[var(--color-text3)]">
            {t("publicProfile.followerAcceptHint", { username })}
          </p>
        </div>
      )}
    </section>
  );
}

function FullProfile({
  profile,
  t,
}: {
  profile: PublicProfileResponse;
  t: TFunction;
}) {
  // EPIC-046 part 2 phase C step 4 — best-effort fetch of recent
  // public posts the user has liked. Silent failure: the section
  // just doesn't render. Capped at 5 for the inline-section UX;
  // future "View all liked posts" page can paginate.
  const [recentLikes, setRecentLikes] = useState<PostRecord[]>([]);
  useEffect(() => {
    let cancelled = false;
    void api
      .listPostsLikedByUser(profile.user.id, { limit: 5 })
      .then((res) => {
        if (cancelled) return;
        setRecentLikes(res.posts);
      })
      .catch(() => {
        if (!cancelled) setRecentLikes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [profile.user.id]);

  // 2026-05-09 — lifetimeVolumeCents (SUM trades.notional_cents) + AUM
  // are matcher-currency cents = USDC post STORY-1700-part-3.
  // CAVEAT: pre-Part-3 BRL rows in the SUM render 5× understated until
  // data-layer backfill ships.
  const displayCurrency = useDisplayCurrency();
  const fxBrl = useFxRate("BRL");
  const usdcPerBrl = fxBrl?.rate ?? null;
  const fmtFx = (cents: string | number): string => {
    const n = typeof cents === "string" ? Number(cents) : cents;
    return formatUsdcCentsForDisplay(n, displayCurrency, usdcPerBrl);
  };
  const volumeUnit =
    displayCurrency === "BRL" ? "R$" : displayCurrency === "USD" ? "$" : "USDC";
  const volumeLabel = t("publicProfile.volumeLabel", { unit: volumeUnit });
  const u = profile.user;
  return (
    <>
      <section className="mb-6 rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5">
        <div className="flex flex-wrap items-center gap-3">
          {u.avatarUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={u.avatarUrl}
              alt=""
              width={56}
              height={56}
              className="h-[56px] w-[56px] flex-shrink-0 rounded-full border border-[var(--color-border)] object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <div
              className="flex h-[56px] w-[56px] flex-shrink-0 items-center justify-center rounded-full text-[24px] font-bold"
              style={{
                background:
                  "color-mix(in srgb, var(--color-brand) 14%, transparent)",
                color: "var(--color-brand)",
              }}
              aria-hidden="true"
            >
              {u.username.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-[22px] font-bold tracking-tight">
                {u.displayName ?? u.username}
              </h1>
              {u.role === "admin" && (
                <span
                  className="rounded-[4px] border px-1.5 py-[2px] text-[10px] font-bold uppercase tracking-[0.4px]"
                  style={{
                    borderColor: "var(--color-brand)",
                    color: "var(--color-brand)",
                    background: "var(--color-brand-tint)",
                  }}
                >
                  {t("publicProfile.granbayTeam")}
                </span>
              )}
              {u.piTier && <PiTierBadge tier={u.piTier} />}
              {u.verifiedExpert && <VerifiedExpertBadge size="md" />}
            </div>
            <p className="mt-1 text-[12px] text-[var(--color-text3)]">
              @{u.username}
              {u.country ? ` · ${u.country}` : ""} ·{" "}
              {t("publicProfile.memberSince", {
                date: new Date(u.memberSince).toLocaleDateString(),
              })}
            </p>
            <p className="mt-2 text-[11px] text-[var(--color-text3)]">
              <strong className="text-[var(--color-text2)]">
                {profile.followerCount.toLocaleString()}
              </strong>{" "}
              {t("publicProfile.followers", {
                count: profile.followerCount,
              })}{" "}
              ·{" "}
              <strong className="text-[var(--color-text2)]">
                {profile.followingCount.toLocaleString()}
              </strong>{" "}
              {t("publicProfile.following")}
            </p>
          </div>
          {!profile.isOwnProfile && (
            <ProfileCtaStack
              username={u.username}
              piTier={u.piTier}
              role={u.role}
              riskScore={u.riskScore}
              riskScoreLimitedHistory={u.riskScoreLimitedHistory}
              subscriptionPriceCents={u.subscriptionPriceCents}
              isOwnProfile={profile.isOwnProfile}
              t={t}
            />
          )}
        </div>

        {u.bio && (
          <p className="mt-4 whitespace-pre-wrap text-[13px] text-[var(--color-text2)]">
            {u.bio}
          </p>
        )}

        {profile.isOwnProfile && (
          <>
            {/* EPIC-047 — own-profile composer entry. Opens the unified
                modal pre-set to "My profile" destination. */}
            <div className="mt-3">
              <ComposerButton
                ariaLabel={t("publicProfile.newPostAria")}
                className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] px-2.5 py-1 text-[11px] font-bold text-[var(--color-text2)] transition-colors hover:border-[var(--color-border2)] hover:text-[var(--color-text)]"
                initialDestination={{ kind: "profile", anonymous: false }}
              >
                + {t("publicProfile.newPost")}
              </ComposerButton>
            </div>
            <p className="mt-4 text-[11px] text-[var(--color-text3)]">
              {t("publicProfile.ownProfilePrefix")}{" "}
              <Link
                href="/settings"
                className="text-[var(--color-brand)] hover:underline"
              >
                {t("publicProfile.editPrivacyDisplay")}
              </Link>
              .
            </p>
          </>
        )}
      </section>

      {(profile.stats.lifetimeTradeCount !== null ||
        profile.stats.lifetimeVolumeCents !== null ||
        profile.stats.winRatePercent !== null) && (
        <section className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {profile.stats.lifetimeTradeCount !== null && (
            <Stat
              label={t("publicProfile.lifetimeTrades")}
              value={profile.stats.lifetimeTradeCount.toLocaleString()}
              highlight
            />
          )}
          {profile.stats.lifetimeVolumeCents !== null && (
            <Stat
              label={volumeLabel}
              value={fmtFx(profile.stats.lifetimeVolumeCents)}
            />
          )}
          {profile.stats.winRatePercent !== null && (
            <Stat
              label={t("publicProfile.winRate")}
              value={`${profile.stats.winRatePercent}%`}
            />
          )}
        </section>
      )}

      {/* EPIC-037 part 2a — PI-conditional sections. Visible only when
          the profile owner is PI-approved (backend returns piMetrics +
          piApplication non-null in that case). Non-PI users see none
          of this. */}
      {profile.piMetrics && (
        <section className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <Stat
            label={t("publicProfile.riskScore")}
            value={
              profile.piMetrics.riskScore !== null
                ? `${profile.piMetrics.riskScore}/10`
                : "—"
            }
            highlight={
              profile.piMetrics.riskScore !== null &&
              profile.piMetrics.riskScore >= 8
            }
          />
          <Stat
            label={t("publicProfile.return30d")}
            value={
              profile.piMetrics.return30dPct !== null
                ? `${profile.piMetrics.return30dPct >= 0 ? "+" : ""}${profile.piMetrics.return30dPct.toFixed(1)}%`
                : "—"
            }
          />
          <Stat
            label={t("publicProfile.winRate30d") || "Win rate"}
            value={
              profile.piMetrics.winRate30dPct !== null
                ? `${profile.piMetrics.winRate30dPct.toFixed(1)}%`
                : "—"
            }
          />
          <Stat
            label={t("publicProfile.maxDrawdown") || "Max drawdown"}
            value={
              profile.piMetrics.drawdownPct !== null
                ? `-${profile.piMetrics.drawdownPct.toFixed(1)}%`
                : "—"
            }
            highlight={
              profile.piMetrics.drawdownPct !== null &&
              profile.piMetrics.drawdownPct >= 30
            }
          />
          <Stat
            label={t("publicProfile.aum")}
            value={
              profile.piMetrics.aumCents !== null
                ? fmtFx(profile.piMetrics.aumCents)
                : "—"
            }
          />
          <Stat
            label={t("publicProfile.copiers")}
            value={profile.piMetrics.activeSubscriberCount.toLocaleString()}
          />
        </section>
      )}
      {profile.piMetrics?.riskScoreLimitedHistory && (
        <p className="mb-4 text-[10px] text-[var(--color-text3)]">
          {t("publicProfile.riskLimitedHistory")}
        </p>
      )}

      {profile.piApplication && (
        <>
          <section className="mb-6 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-5">
            <h2 className="mb-2 text-[14px] font-bold">
              {t("publicProfile.aboutHeading")}
            </h2>
            <p className="whitespace-pre-wrap text-[13px] text-[var(--color-text2)]">
              {profile.piApplication.motivation}
            </p>
          </section>

          <section className="mb-6 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-5">
            <h2 className="mb-2 text-[14px] font-bold">
              {t("publicProfile.trackRecordHeading")}
            </h2>
            <p className="whitespace-pre-wrap text-[13px] text-[var(--color-text2)]">
              {profile.piApplication.trackRecord}
            </p>
          </section>

          {profile.piApplication.externalLinks.length > 0 && (
            <section className="mb-6 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-5">
              <h2 className="mb-2 text-[14px] font-bold">
                {t("publicProfile.linksHeading")}
              </h2>
              <ul className="space-y-1.5 text-[12px]">
                {profile.piApplication.externalLinks.map((link, i) => (
                  <li key={i}>
                    <a
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-mono text-[var(--color-brand)] hover:underline"
                    >
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {/* EPIC-037 part 2b — viewer's copy-subscription state, when
          they're actively copying this PI. Self-defends: only fetches
          when not own profile, viewer is authed, and the profile owner
          is PI-approved. */}
      {!profile.isOwnProfile &&
        (profile.user.piTier !== null || profile.user.role === "creator") && (
          <CopyStatePanel
            piUsername={profile.user.username}
            fmtFx={fmtFx}
            t={t}
          />
        )}

      {profile.recentTrades && profile.recentTrades.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[var(--color-text3)]">
            {t("publicProfile.recentTradesHeading")}
          </h2>
          <div className="overflow-x-auto rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wide text-[var(--color-text3)]">
                  <th className="px-3 py-2 font-semibold">
                    {t("publicProfile.colMarket")}
                  </th>
                  <th className="px-3 py-2 font-semibold">
                    {t("publicProfile.colSide")}
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    {t("publicProfile.colSize")}
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    {t("publicProfile.colPrice")}
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">
                    {t("publicProfile.colWhen")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {profile.recentTrades.map((t, i) => (
                  <tr
                    key={`${t.marketSlug}-${t.tradedAt}-${i}`}
                    className="border-t border-[var(--color-border)]"
                  >
                    <td className="max-w-[220px] truncate px-3 py-2">
                      <Link
                        href={`/markets/${t.marketSlug}`}
                        className="hover:underline"
                      >
                        {t.marketTitleEn ?? t.marketSlug}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className="rounded-[4px] px-1.5 py-[2px] text-[10px] font-bold uppercase tracking-[0.4px]"
                        style={{
                          color:
                            t.side === "buy"
                              ? "var(--color-yes)"
                              : "var(--color-no)",
                          background:
                            t.side === "buy"
                              ? "color-mix(in srgb, var(--color-yes) 14%, transparent)"
                              : "color-mix(in srgb, var(--color-no) 14%, transparent)",
                        }}
                      >
                        {t.side}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-[var(--color-text2)]">
                      {Number(t.sizeShares).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-[var(--color-text2)]">
                      {t.priceCents}¢
                    </td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums text-[var(--color-text3)]">
                      {new Date(t.tradedAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {profile.recentTrades && profile.recentTrades.length === 0 && (
        <section className="rounded-md border border-dashed border-[var(--color-border)] bg-[var(--color-bg)] p-4 text-[12px] text-[var(--color-text3)]">
          {t("publicProfile.noTradesYet")}
        </section>
      )}

      {/* EPIC-046 part 1a/1b — Recent activity. Two sub-lists:
          authored discussions + replies the user posted on others'
          discussions. Both visible-only at the API layer. */}
      <section className="mt-6">
        <h2 className="mb-2 text-[13px] font-semibold uppercase tracking-wide text-[var(--color-text3)]">
          {t("publicProfile.recentActivityHeading")}
        </h2>
        {profile.recentDiscussions.length === 0 &&
        profile.recentDiscussionReplies.length === 0 &&
        profile.recentMarketComments.length === 0 ? (
          <div className="rounded-md border border-dashed border-[var(--color-border)] bg-[var(--color-bg)] p-4 text-[12px] text-[var(--color-text3)]">
            {t("publicProfile.noActivityYet")}
          </div>
        ) : (
          <div className="space-y-3">
            {profile.recentDiscussions.length > 0 && (
              <ul className="space-y-2">
                {profile.recentDiscussions.map((d) => (
                  <li key={d.id}>
                    <Link
                      href={`/topic/${d.topicSlug}/d/${d.id}`}
                      className="block rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2.5 transition-colors hover:border-[var(--color-brand)]"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="rounded-[3px] border px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-[0.4px]"
                          style={{
                            borderColor: "var(--color-text3)",
                            color: "var(--color-text3)",
                          }}
                        >
                          {t("publicProfile.tagPosted")}
                        </span>
                        <div className="min-w-0 flex-1 truncate text-[12px] font-semibold text-[var(--color-text)]">
                          {d.title}
                        </div>
                      </div>
                      {d.bodyExcerpt && (
                        <p className="mt-1 line-clamp-2 text-[11px] text-[var(--color-text2)]">
                          {d.bodyExcerpt}
                        </p>
                      )}
                      <div className="mt-1.5 flex items-center gap-2 text-[10px] text-[var(--color-text3)]">
                        <span>/topic/{d.topicSlug}</span>
                        <span>·</span>
                        <span>
                          {t("publicProfile.replies", {
                            count: d.replyCount,
                          })}
                        </span>
                        <span>·</span>
                        <span>
                          {new Date(d.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {profile.recentDiscussionReplies.length > 0 && (
              <ul className="space-y-2">
                {profile.recentDiscussionReplies.map((r) => (
                  <li key={r.id}>
                    <Link
                      href={`/topic/${r.topicSlug}/d/${r.discussionId}`}
                      className="block rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2.5 transition-colors hover:border-[var(--color-brand)]"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="rounded-[3px] border px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-[0.4px]"
                          style={{
                            borderColor: "var(--color-text3)",
                            color: "var(--color-text3)",
                          }}
                        >
                          {t("publicProfile.tagReplied")}
                        </span>
                        <div className="min-w-0 flex-1 truncate text-[12px] text-[var(--color-text2)]">
                          {t("publicProfile.onPrefix")}{" "}
                          <span className="font-semibold text-[var(--color-text)]">
                            {r.discussionTitle}
                          </span>
                        </div>
                      </div>
                      {r.bodyExcerpt && (
                        <p className="mt-1 line-clamp-2 text-[11px] text-[var(--color-text2)]">
                          {r.bodyExcerpt}
                        </p>
                      )}
                      <div className="mt-1.5 flex items-center gap-2 text-[10px] text-[var(--color-text3)]">
                        <span>/topic/{r.topicSlug}</span>
                        <span>·</span>
                        <span>
                          {new Date(r.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {/* EPIC-046 part 1c — recent comments authored by the
                user on a market. Distinct surface from topic
                discussions (1a) and replies (1b); links to the
                market detail page where the comment thread lives. */}
            {profile.recentMarketComments.length > 0 && (
              <ul className="space-y-2">
                {profile.recentMarketComments.map((c) => (
                  <li key={c.id}>
                    <Link
                      href={`/markets/${c.marketSlug}`}
                      className="block rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2.5 transition-colors hover:border-[var(--color-brand)]"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="rounded-[3px] border px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-[0.4px]"
                          style={{
                            borderColor: "var(--color-text3)",
                            color: "var(--color-text3)",
                          }}
                        >
                          {t("publicProfile.tagCommented")}
                        </span>
                        <div className="min-w-0 flex-1 truncate text-[12px] text-[var(--color-text2)]">
                          {t("publicProfile.onPrefix")}{" "}
                          <span className="font-semibold text-[var(--color-text)]">
                            {c.marketTitle}
                          </span>
                        </div>
                      </div>
                      {c.bodyExcerpt && (
                        <p className="mt-1 line-clamp-2 text-[11px] text-[var(--color-text2)]">
                          {c.bodyExcerpt}
                        </p>
                      )}
                      <div className="mt-1.5 flex items-center gap-2 text-[10px] text-[var(--color-text3)]">
                        <span>/markets/{c.marketSlug}</span>
                        <span>·</span>
                        <span>
                          {new Date(c.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      {/* EPIC-046 part 2 phase C step 4 — recently liked posts.
          Public posts only (visibility-gated server-side). Hidden
          when the user has no public liked posts. */}
      {recentLikes.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-2 text-[13px] font-semibold uppercase tracking-wide text-[var(--color-text3)]">
            {t("publicProfile.recentlyLikedHeading")}
          </h2>
          <ul className="space-y-2">
            {recentLikes.map((p) => (
              <li key={p.id}>
                <Link
                  href={`/posts/${p.id}`}
                  className="block rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2.5 transition-colors hover:border-[var(--color-brand)]"
                >
                  <div className="flex items-center gap-2">
                    <span className="rounded-[3px] border border-[var(--color-text3)] px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
                      {t("publicProfile.tagLiked")}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-[var(--color-text)]">
                      {p.title ?? p.bodyMd.slice(0, 80)}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-[10px] text-[var(--color-text3)]">
                    <span>@{p.authorUsername}</span>
                    <span>·</span>
                    <span>{new Date(p.createdAt).toLocaleDateString()}</span>
                    {p.likeCount > 0 && (
                      <>
                        <span>·</span>
                        <span>
                          {t("publicProfile.likes", { count: p.likeCount })}
                        </span>
                      </>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* EPIC-046 part 1 — chronological activity timeline backed by
          feed_events. Unifies posts/likes/comments/replies/copy-sub
          starts in occurred_at DESC order. Coexists with the legacy
          per-section lists above; once feed_events backfills every
          historical row, the legacy lists deprecate cleanly. */}
      <section className="mt-6">
        <h2 className="mb-2 text-[13px] font-semibold uppercase tracking-wide text-[var(--color-text3)]">
          {t("activityTimeline.heading")}
        </h2>
        <ActivityTimeline handle={profile.user.username} />
      </section>
    </>
  );
}

/**
 * EPIC-037 part 1 — three independent CTAs on the merged profile.
 *
 * The founder spec explicitly separates following / subscribing /
 * copying as three different products. Render rules:
 *
 *   Follow:    always visible (when not self-view)
 *   Subscribe: visible when the creator has a paid tier configured
 *              (subscriptionPriceCents !== null)
 *   Copy:      visible when the profile owner is PI-approved
 *              (piTier !== null OR role === 'creator') AND riskScore
 *              passes the founder hard rule (riskScore < 8 OR riskScore
 *              still uncomputed). Disabled with tooltip when riskScore
 *              >= 8.
 *
 * Self-view hides all three (handled by the !isOwnProfile branch in
 * the parent, but we re-defend here so any future caller can pass
 * through cleanly).
 *
 * Copy click opens SubscribeToPiModal inline. Once the modal lands
 * on a successful subscription, the page reloads via window.location
 * to refresh the "you're copying" indicator (the inline panel arrives
 * with EPIC-037 part 2).
 */
function ProfileCtaStack({
  username,
  piTier,
  role,
  riskScore,
  riskScoreLimitedHistory,
  subscriptionPriceCents,
  isOwnProfile,
  t,
}: {
  username: string;
  piTier: PublicProfileResponse["user"]["piTier"];
  role: string;
  riskScore: number | null;
  riskScoreLimitedHistory: boolean;
  subscriptionPriceCents: string | null;
  isOwnProfile: boolean;
  t: TFunction;
}) {
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  // STORY-503 phase 2 — local sub fetch so the header Copy button can
  // flip to "Copying since {date}" when the viewer already has an
  // active sub. The CopyStatePanel below does its own fetch for the
  // detail card; both fetches are cheap (single endpoint, lc cache).
  const [sub, setSub] = useState<CopySubscription | null>(null);
  useEffect(() => {
    if (isOwnProfile) return;
    let cancelled = false;
    void api
      .listMyCopySubscriptions(false)
      .then((subs) => {
        if (cancelled) return;
        const match =
          subs.find((s) => s.piUsername === username && !s.unsubscribedAt) ??
          null;
        setSub(match);
      })
      .catch(() => {
        if (!cancelled) setSub(null);
      });
    return () => {
      cancelled = true;
    };
  }, [username, isOwnProfile]);
  if (isOwnProfile) return null;
  const isPiApproved = piTier !== null || role === "creator";
  // Founder hard rule: riskScore >= 8 disables Copy. NULL = uncomputed
  // (fresh PI before the daily cron has run); permissive default so
  // we don't block copy on a brand-new approved PI.
  const copyDisabled = riskScore !== null && riskScore >= 8;
  return (
    <>
      <div className="flex flex-col items-end gap-2">
        <FollowButton username={username} />
        {subscriptionPriceCents && (
          <SubscribeButton
            creatorUsername={username}
            monthlyPriceCents={subscriptionPriceCents}
            isOwnProfile={isOwnProfile}
          />
        )}
        {isPiApproved &&
          (sub ? (
            // STORY-503 phase 2 — when the viewer is already copying this
            // PI, replace the header Copy button with a "Copying since
            // {date}" pill that links to their activity page. Mirrors
            // Twitter "Following" / eToro "Copying since" patterns.
            <Link
              href={`/portfolio/copy/${sub.id}/activity`}
              className="inline-flex min-h-[40px] items-center rounded-md border border-[#2d8a5d] bg-[color-mix(in_srgb,#2d8a5d_12%,transparent)] px-3 py-1.5 text-[12px] font-bold text-[#2d8a5d] transition-opacity hover:opacity-90 md:min-h-0"
            >
              {t("publicProfile.copyingSince", {
                date: new Date(sub.createdAt).toLocaleDateString(),
              })}
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => {
                if (!copyDisabled) setCopyModalOpen(true);
              }}
              disabled={copyDisabled}
              title={
                copyDisabled
                  ? t("publicProfile.copyDisabledTooltip", {
                      score: riskScore,
                    })
                  : riskScoreLimitedHistory
                    ? t("publicProfile.riskLimitedHistory")
                    : undefined
              }
              className="inline-flex min-h-[40px] items-center rounded-md border px-3 py-1.5 text-[12px] font-bold transition-colors disabled:opacity-50 md:min-h-0"
              style={{
                borderColor: copyDisabled
                  ? "var(--color-border)"
                  : "var(--color-brand)",
                color: copyDisabled ? "var(--color-text3)" : "var(--color-brand)",
                background: copyDisabled
                  ? "transparent"
                  : "var(--color-brand-tint)",
              }}
            >
              {copyDisabled
                ? t("publicProfile.copyUnavailable")
                : t("publicProfile.copy")}
            </button>
          ))}
      </div>
      {copyModalOpen && (
        <SubscribeToPiModal
          piUsername={username}
          existing={null}
          onClose={() => setCopyModalOpen(false)}
          onSuccess={() => {
            setCopyModalOpen(false);
            // Reload picks up the "you're copying" indicator that
            // EPIC-037 part 2 will surface inline. For now the
            // SubscribeButton/FollowButton already handle their own
            // optimistic UI; this just keeps the rest of the page
            // honest about the new subscription.
            window.location.reload();
          }}
        />
      )}
    </>
  );
}

/**
 * EPIC-037 part 2b — "you're copying X" inline panel.
 *
 * When the viewer has an active copy subscription against this PI,
 * surface allocation / committed / paused state + Edit / Pause /
 * Resume / Unsubscribe controls. Mirrors the panel that lived on
 * /pi/[username] so the redirect from /pi/* → /u/* (part 3) lands
 * with feature parity.
 *
 * Self-defends:
 *   - Skips fetch if not authed (Copy CTA can't apply anyway).
 *   - Skips fetch if isOwnProfile (parent already filters).
 *   - Renders nothing while fetching, on error, or with no active
 *     subscription.
 */
function CopyStatePanel({
  piUsername,
  fmtFx,
  t,
}: {
  piUsername: string;
  fmtFx: (cents: string | number) => string;
  t: TFunction;
}) {
  const [authed, setAuthed] = useState(false);
  const [sub, setSub] = useState<CopySubscription | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAuthed(loadTokens() !== null);
  }, []);

  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    void api
      .listMyCopySubscriptions(false)
      .then((rows) => {
        if (cancelled) return;
        const match = rows.find(
          (r) => r.piUsername === piUsername && r.unsubscribedAt === null,
        );
        setSub(match ?? null);
      })
      .catch(() => {
        if (!cancelled) setSub(null);
      });
    return () => {
      cancelled = true;
    };
  }, [authed, piUsername]);

  if (!sub) return null;

  async function togglePause() {
    if (!sub || pending) return;
    setPending(true);
    setActionMsg(null);
    try {
      const next = sub.paused
        ? await api.resumeCopySubscription(sub.id)
        : await api.pauseCopySubscription(sub.id);
      setSub(next);
      setActionMsg(
        next.paused
          ? t("publicProfile.msgSubPaused")
          : t("publicProfile.msgSubResumed"),
      );
    } catch (err) {
      setActionMsg(
        err instanceof Error ? err.message : t("publicProfile.msgUpdateFailed"),
      );
    } finally {
      setPending(false);
    }
  }

  async function unsubscribe() {
    if (!sub || pending) return;
    if (!window.confirm(t("publicProfile.confirmStopCopy", { piUsername })))
      return;
    setPending(true);
    setActionMsg(null);
    try {
      await api.unsubscribeFromPi(sub.id);
      setSub(null);
      setActionMsg(t("publicProfile.msgUnsubscribed", { piUsername }));
    } catch (err) {
      setActionMsg(
        err instanceof Error
          ? err.message
          : t("publicProfile.msgUnsubscribeFailed"),
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <>
      <section className="mb-6 rounded-md border border-[var(--color-brand)] bg-[var(--color-brand-tint)] p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-[14px] font-bold">
            {t("publicProfile.youAreCopying", { piUsername })}
            {sub.paused && (
              <span className="ml-2 rounded-[4px] border border-[var(--color-text3)] px-1.5 py-[1px] text-[10px] uppercase tracking-[0.4px] text-[var(--color-text3)]">
                {t("publicProfile.pausedTag")}
              </span>
            )}
          </h2>
          <span className="text-[11px] text-[var(--color-text3)]">
            {t("publicProfile.sinceDate", {
              date: new Date(sub.createdAt).toLocaleDateString(),
            })}
          </span>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 text-[12px] text-[var(--color-text2)]">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
              {t("publicProfile.allocation")}
            </div>
            <div className="mt-0.5 font-bold tabular-nums">
              {fmtFx(sub.allocationCents)}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
              {t("publicProfile.currentlyCommitted")}
            </div>
            <div className="mt-0.5 font-bold tabular-nums">
              {fmtFx(sub.committedCents)}
            </div>
          </div>
        </div>
        {(sub.filters.minPriceCents !== undefined ||
          sub.filters.maxPriceCents !== undefined ||
          (sub.filters.outcomeKinds &&
            sub.filters.outcomeKinds.length === 1)) && (
          <p className="mt-3 text-[11px] text-[var(--color-text3)]">
            {t("publicProfile.filtersLabel")}
            {sub.filters.minPriceCents !== undefined ||
            sub.filters.maxPriceCents !== undefined
              ? ` ${sub.filters.minPriceCents ?? "—"}¢ to ${sub.filters.maxPriceCents ?? "—"}¢`
              : ""}
            {sub.filters.outcomeKinds && sub.filters.outcomeKinds.length === 1
              ? ` · ${t("publicProfile.outcomeOnly", {
                  kind: sub.filters.outcomeKinds[0].toUpperCase(),
                })}`
              : ""}
          </p>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          {/* STORY-503 phase 2 — primary CTA for active subscribers: jump
              to the activity page that surfaces P&L, open positions, and
              the trade history for THIS sub. Higher priority than Edit
              because the most common subscriber action is "check progress",
              not "modify settings". */}
          <Link
            href={`/portfolio/copy/${sub.id}/activity`}
            className="inline-flex min-h-[40px] items-center rounded-md border border-[#2d8a5d] bg-[#2d8a5d] px-3 py-1.5 text-[11px] font-bold text-white transition-opacity hover:opacity-90 md:min-h-0"
          >
            {t("publicProfile.btnViewActivity")}
          </Link>
          <button
            type="button"
            disabled={pending}
            onClick={() => setEditOpen(true)}
            className="inline-flex min-h-[40px] items-center rounded-md border border-[var(--color-brand)] bg-[var(--color-bg)] px-3 py-1.5 text-[11px] font-bold text-[var(--color-brand)] transition-colors hover:bg-[var(--color-brand-tint)] disabled:opacity-50 md:min-h-0"
          >
            {t("publicProfile.btnEdit")}
          </button>
          <button
            type="button"
            disabled={pending}
            onClick={togglePause}
            className="inline-flex min-h-[40px] items-center rounded-md border border-[var(--color-border)] px-3 py-1.5 text-[11px] font-bold text-[var(--color-text2)] transition-colors hover:border-[var(--color-brand)] disabled:opacity-50 md:min-h-0"
          >
            {sub.paused
              ? t("publicProfile.btnResume")
              : t("publicProfile.btnPause")}
          </button>
          <button
            type="button"
            disabled={pending}
            onClick={unsubscribe}
            className="inline-flex min-h-[40px] items-center rounded-md border border-[var(--color-no)] px-3 py-1.5 text-[11px] font-bold transition-colors hover:bg-[var(--color-no)] hover:text-white disabled:opacity-50 md:min-h-0"
            style={{ color: "var(--color-no)" }}
          >
            {t("publicProfile.btnUnsubscribe")}
          </button>
        </div>
        {actionMsg && (
          <p className="mt-2 text-[11px] text-[var(--color-text3)]">
            {actionMsg}
          </p>
        )}
      </section>
      {editOpen && (
        <SubscribeToPiModal
          piUsername={piUsername}
          existing={sub}
          onClose={() => setEditOpen(false)}
          onSuccess={(updated) => {
            setEditOpen(false);
            setSub(updated);
            setActionMsg(t("publicProfile.msgSubUpdated"));
          }}
        />
      )}
    </>
  );
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-3"
      style={highlight ? { borderColor: "var(--color-brand)" } : undefined}
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.4px] text-[var(--color-text3)]">
        {label}
      </div>
      <div className="mt-1 text-[20px] font-bold tabular-nums">{value}</div>
    </div>
  );
}

// Tier badge — DRY against /lib/piTier.ts (the single source of
// truth for the bronze/silver/gold/diamond → Cadet/Champion/Elite/
// Elite Pro remap shared across /pi/leaderboard, /pi/[username],
// and this page).
function PiTierBadge({
  tier,
}: {
  tier: "bronze" | "silver" | "gold" | "diamond";
}) {
  const meta = piTierMeta(tier);
  return (
    <span
      className="rounded-[4px] border px-1.5 py-[2px] text-[10px] font-bold uppercase tracking-[0.4px]"
      style={{
        borderColor: meta.border,
        color: meta.color,
        background: meta.bg,
      }}
    >
      {meta.label}
    </span>
  );
}
